import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Box, Button, TextField, Typography, Paper, IconButton, Chip, ChipProps, Divider, Alert, MenuItem, Select, FormControl, InputLabel, Card, CardContent, Container, Stack, Tooltip, Grid, Accordion, AccordionSummary, AccordionDetails } from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import RefreshIcon from '@mui/icons-material/Refresh';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import SecurityIcon from '@mui/icons-material/Security';
import VpnKeyIcon from '@mui/icons-material/VpnKey';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import AssignmentIcon from '@mui/icons-material/Assignment';
import CheckIcon from '@mui/icons-material/Check';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import TipsAndUpdatesIcon from '@mui/icons-material/TipsAndUpdates';
import { useMsal } from '@azure/msal-react';

interface OidcMetadataInspectorProps { onBack: () => void; }
interface OidcMetadata { issuer?: string; authorization_endpoint?: string; token_endpoint?: string; jwks_uri?: string; end_session_endpoint?: string; userinfo_endpoint?: string; response_types_supported?: string[]; subject_types_supported?: string[]; id_token_signing_alg_values_supported?: string[]; scopes_supported?: string[]; claims_supported?: string[]; token_endpoint_auth_methods_supported?: string[]; [k:string]: any; }
interface Jwk { kid?: string; kty?: string; use?: string; alg?: string; n?: string; e?: string; x?: string; y?: string; crv?: string; x5t?: string; [k:string]: any; }
interface JwksResponse { keys?: Jwk[]; [k:string]: any; }
interface JwtDecoded { header: any; payload: any; }
interface KeyMatch { key: Jwk; matchReasons: string[]; }

const base64UrlDecode = (input: string) => {
  try {
    input = input.replace(/-/g, '+').replace(/_/g, '/');
    const pad = input.length % 4; if (pad) input += '='.repeat(4-pad);
    return atob(input);
  } catch { return ''; }
};

const decodeJwt = (token: string): JwtDecoded | null => {
  const parts = token.split('.');
  if (parts.length < 2) return null;
  try {
    const header = JSON.parse(base64UrlDecode(parts[0]));
    const payload = JSON.parse(base64UrlDecode(parts[1]));
    return { header, payload };
  } catch { return null; }
};

async function verifyRS256(token: string, jwk: Jwk): Promise<boolean> {
  try {
    if (!jwk.n || !jwk.e) return false;
    const [headerB64, payloadB64, signatureB64] = token.split('.');
    if (!signatureB64) return false;
    const derSig = Uint8Array.from(atob(signatureB64.replace(/-/g,'+').replace(/_/g,'/')), c=> c.charCodeAt(0));
    const modulus = Uint8Array.from(base64UrlDecode(jwk.n), c=> c.charCodeAt(0));
    const exponent = Uint8Array.from(base64UrlDecode(jwk.e), c=> c.charCodeAt(0));
    // Import JWK key
    const key = await crypto.subtle.importKey('jwk', { kty: 'RSA', n: jwk.n, e: jwk.e, alg: 'RS256', ext: true }, { name: 'RSASSA-PKCS1-v1_5', hash: 'SHA-256' }, false, ['verify']);
    const data = new TextEncoder().encode(headerB64 + '.' + payloadB64);
    const ok = await crypto.subtle.verify('RSASSA-PKCS1-v1_5', key, derSig, data);
    return ok;
  } catch { return false; }
}

const OidcMetadataInspector: React.FC<OidcMetadataInspectorProps> = ({ onBack }) => {
  const { accounts } = useMsal();
  const currentTenantId = accounts.length > 0 ? (accounts[0] as any).tenantId : null;

  // Tenant selection model (similar pattern to RawOAuthTester)
  const tenantOptions = useMemo(() => [
    { value: 'common', label: 'common (Multi-tenant + Personal)' },
    { value: 'organizations', label: 'organizations (Multi-tenant)' },
    { value: 'consumers', label: 'consumers (Personal accounts)' },
    ...(currentTenantId ? [{ value: currentTenantId, label: `${currentTenantId} (Current tenant)` }] : []),
    { value: '__custom__', label: 'Custom tenant / B2C / Full URL' }
  ], [currentTenantId]);

  const [tenantSelection, setTenantSelection] = useState<string>(currentTenantId || 'common');
  const [customAuthority, setCustomAuthority] = useState<string>('');
  const [authorityInput, setAuthorityInput] = useState<string>('https://login.microsoftonline.com/common/v2.0');
  const [wellKnownUrl, setWellKnownUrl] = useState<string>('');
  const [loading, setLoading] = useState(false);
  const [metadata, setMetadata] = useState<OidcMetadata | null>(null);
  const [jwks, setJwks] = useState<JwksResponse | null>(null);
  const [jwksLoading, setJwksLoading] = useState(false);
  const [jwtInput, setJwtInput] = useState<string>('');
  const [decodedJwt, setDecodedJwt] = useState<JwtDecoded | null>(null);
  const [keyMatches, setKeyMatches] = useState<KeyMatch[]>([]);
  const [verifying, setVerifying] = useState(false);
  const [signatureValid, setSignatureValid] = useState<boolean | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [snackMessage, setSnackMessage] = useState<string>('');

  const tokenAnalysis = useMemo(() => {
    if (!decodedJwt) return null;
    const header = decodedJwt.header || {};
    const payload = decodedJwt.payload || {};

    // Common claims across providers
    const hasScopes = typeof payload.scp === 'string' && payload.scp.trim().length > 0;
    const hasRoles = Array.isArray(payload.roles) && payload.roles.length > 0;

    // Header and payload type hints (Azure AD often uses header.typ = "JWT", sometimes payload.typ = "at+jwt")
    const hdrTyp = typeof header.typ === 'string' ? String(header.typ).toLowerCase() : '';
    const pldTyp = typeof payload.typ === 'string' ? String(payload.typ).toLowerCase() : '';
    const tokenUse = typeof payload.token_use === 'string' ? String(payload.token_use).toLowerCase() : '';
    const idTyp = typeof payload.idtyp === 'string' ? String(payload.idtyp).toLowerCase() : '';

    // Audience vs client identifiers (helps distinguish access vs id tokens)
    const audience = payload.aud;
    const clientIds = [payload.appid, payload.azp, payload.client_id].filter(Boolean);
    const isAudienceArray = Array.isArray(audience);
    const isAudOneOfClientIds = clientIds.length > 0 && (
      isAudienceArray
        ? (audience as string[]).some(a => clientIds.includes(a))
        : clientIds.includes(audience)
    );

    // Access token heuristics
    const isAccessByTyp = hdrTyp.includes('at+jwt') || pldTyp.includes('at+jwt') || pldTyp === 'access';
    const isAccessByUse = tokenUse === 'access' || idTyp === 'app';
    const isAccessByAud = clientIds.length > 0 && !isAudOneOfClientIds; // audience different from client id(s) => likely resource audience
    const isAccess = hasScopes || hasRoles || isAccessByTyp || isAccessByUse || isAccessByAud;

    if (isAccess) {
      const hasBoth = hasScopes && hasRoles;
      const label = hasBoth
        ? 'Access Token (Scopes + Roles)'
        : hasScopes
          ? 'Access Token (Delegated)'
          : 'Access Token (App Roles)';
      let hint = '💡 This appears to be an Access Token.';
      if (hasBoth) {
        hint = '💡 Access Token with delegated scopes and app roles. Delegated user access plus app role assignments are present.';
      } else if (hasScopes) {
        hint = '💡 Access Token with delegated permissions (scopes). Represents a user acting on their own behalf.';
      } else if (hasRoles) {
        hint = '💡 Access Token with application permissions (roles). May represent app-only access or assigned app roles.';
      }
      return { label, color: 'success' as ChipProps['color'], hint };
    }

    // ID token heuristics
    const isIdByTyp = pldTyp.includes('id');
    const isIdByUse = tokenUse === 'id' || idTyp === 'user';
    const hasIdOnlyHints = Boolean(payload.nonce) || Boolean(payload.amr) || isAudOneOfClientIds;
    const isId = isIdByTyp || isIdByUse || hasIdOnlyHints;

    if (isId) {
      return {
        label: 'ID Token (OpenID Connect)',
        color: 'primary' as ChipProps['color'],
        hint: '💡 This appears to be an ID Token used for user authentication. It contains identity claims about the signed-in user.'
      };
    }

    return {
      label: payload.typ ? String(payload.typ) : 'JWT',
      color: undefined as ChipProps['color'] | undefined,
      hint: '💡 JWT decoded successfully. Review the claims above to understand token purpose and permissions.'
    };
  }, [decodedJwt]);

  const openSnack = (message: string, _severity?: 'success' | 'info' | 'error') => {
    setSnackMessage(message);
    setTimeout(() => setSnackMessage(''), 2000);
  };

  const computeWellKnown = useCallback((base: string) => {
    let trimmed = base.trim().replace(/\/$/, '');
    // Azure AD v2 endpoints typically already end with /v2.0; ensure .well-known path appended
    return `${trimmed}/.well-known/openid-configuration`;
  }, []);

  useEffect(() => { setWellKnownUrl(computeWellKnown(authorityInput)); }, [authorityInput, computeWellKnown]);

  // Update authorityInput when tenant selection changes (unless custom)
  useEffect(() => {
    if (tenantSelection === '__custom__') {
      if (customAuthority.trim()) {
        setAuthorityInput(customAuthority.trim());
      }
    } else {
      setAuthorityInput(`https://login.microsoftonline.com/${tenantSelection}/v2.0`);
    }
  }, [tenantSelection, customAuthority]);

  const fetchMetadata = async () => {
    setLoading(true); setError(null); setMetadata(null); setJwks(null); setDecodedJwt(null); setKeyMatches([]); setSignatureValid(null);
    try {
      const res = await fetch(wellKnownUrl, { cache: 'no-store' });
      if (!res.ok) throw new Error(`Failed (${res.status})`);
      const json = await res.json();
      setMetadata(json);
    } catch (e:any) {
      setError(e.message || 'Failed to load metadata');
    } finally { setLoading(false); }
  };

  const fetchJwks = async () => {
    if (!metadata?.jwks_uri) return;
    setJwksLoading(true); setError(null); setJwks(null); setKeyMatches([]); setSignatureValid(null);
    try {
      const res = await fetch(metadata.jwks_uri, { cache: 'no-store' });
      if (!res.ok) throw new Error(`JWKS failed (${res.status})`);
      const json = await res.json();
      setJwks(json);
    } catch (e:any) {
      setError(e.message || 'Failed to load JWKS');
    } finally { setJwksLoading(false); }
  };

  const analyzeJwt = () => {
    setSignatureValid(null);
    const decoded = decodeJwt(jwtInput.trim());
    setDecodedJwt(decoded);
    if (!decoded) { setKeyMatches([]); return; }
    const matches: KeyMatch[] = [];
    if (jwks?.keys) {
      jwks.keys.forEach(k => {
        const reasons: string[] = [];
        if (k.kid && decoded.header?.kid && k.kid === decoded.header.kid) reasons.push('kid match');
        if (k.alg && decoded.header?.alg && k.alg === decoded.header.alg) reasons.push('alg match');
        if (reasons.length) matches.push({ key: k, matchReasons: reasons });
      });
    }
    setKeyMatches(matches);
  };

  const performSignatureVerification = async () => {
    if (!decodedJwt || !jwks?.keys) return;
    setVerifying(true); setSignatureValid(null);
    try {
      const header = decodedJwt.header;
      if (header.alg !== 'RS256') { setSignatureValid(false); return; }
      const candidate = jwks.keys.find(k => k.kid === header.kid && k.kty === 'RSA' && k.n && k.e);
      if (!candidate) { setSignatureValid(false); return; }
      const ok = await verifyRS256(jwtInput.trim(), candidate);
      setSignatureValid(ok);
    } finally { setVerifying(false); }
  };

  const copy = (text: string) => navigator.clipboard.writeText(text);

  const readinessScore = useMemo(() => {
    if (!metadata) return 0;
    let score = 0;
    if (metadata.authorization_endpoint) score += 10;
    if (metadata.token_endpoint) score += 10;
    if (metadata.jwks_uri) score += 10;
    if (metadata.issuer) score += 15;
    if (Array.isArray(metadata.response_types_supported)) score += 5;
    if (Array.isArray(metadata.scopes_supported)) score += 5;
    if (Array.isArray(metadata.claims_supported)) score += 5;
    if (metadata.end_session_endpoint) score += 5;
    return Math.min(100, score);
  }, [metadata]);

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 6 }}>
      {/* Simple snack notification */}
      {snackMessage && (
        <Box sx={{ position: 'fixed', bottom: 16, left: '50%', transform: 'translateX(-50%)', zIndex: 9999, bgcolor: 'primary.main', color: 'white', px: 3, py: 1.5, borderRadius: 2, boxShadow: 3 }}>
          <Typography variant="body2">{snackMessage}</Typography>
        </Box>
      )}
      {/* Header Card */}
      <Card variant="outlined" sx={{ mb: 4, borderRadius: 2, overflow: 'hidden' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', p: 2, backgroundColor: '#f4f8fb', borderBottom: '1px solid rgba(0, 0, 0, 0.12)' }}>
          <Button
            variant="outlined"
            startIcon={<ArrowBackIcon />}
            onClick={onBack}
            sx={{
              mr: 2,
              borderRadius: 2,
              pl: 1.5,
              color: 'text.secondary',
              borderColor: 'divider',
              '&:hover': { backgroundColor: 'rgba(0,0,0,0.04)', borderColor: 'primary.main', color: 'primary.main' }
            }}
          >
            Back
          </Button>
          <Box sx={{ borderLeft: '4px solid', borderColor: 'primary.main', pl: 2, py: 0.5 }}>
            <Typography variant="h4" sx={{ fontWeight: 600, color: 'primary.main', fontFamily: "'Segoe UI', Roboto, 'Open Sans', sans-serif", letterSpacing: '-0.01em' }}>
              OIDC Metadata & JWKS Inspector
            </Typography>
            <Typography variant="body2" sx={{ color: 'text.secondary', fontFamily: "'Segoe UI', Roboto, 'Open Sans', sans-serif" }}>
              Inspect OpenID Connect discovery documents, JWKS, and verify JWT signatures
            </Typography>
          </Box>
        </Box>
        {metadata && (
          <Box sx={{ px: 2, py: 1, display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
            <Chip label="Discovery" size="small" variant="outlined" />
            <Chip label={`Score ${readinessScore}/100`} size="small" color={readinessScore > 50 ? 'success' : 'warning'} />
            {signatureValid !== null && (
              <Chip size="small" color={signatureValid ? 'success' : 'error'} label={signatureValid ? 'Signature Valid' : 'Signature Invalid'} />
            )}
          </Box>
        )}
      </Card>



      {/* Authority Selection */}
      <Card variant="outlined" sx={{ borderRadius: 2, mb: 3 }}>
        <CardContent sx={{ p: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', pb: 1, mb: 3, borderBottom: '1px solid', borderColor: 'divider' }}>
            <VpnKeyIcon color="primary" sx={{ mr: 1 }} />
            <Typography variant="h6" sx={{ fontWeight: 600 }}>Authority</Typography>
          </Box>
          <Grid container spacing={3}>
            <Grid item xs={12} md={4}>
              <FormControl fullWidth size="small">
                <InputLabel>Tenant / Authority</InputLabel>
                <Select value={tenantSelection} label="Tenant / Authority" onChange={(e)=> setTenantSelection(e.target.value)}>
                  {tenantOptions.map(o => <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>)}
                </Select>
              </FormControl>
            </Grid>
            {tenantSelection === '__custom__' && (
              <Grid item xs={12} md={8}>
                <TextField
                  label="Custom Authority (Full Base URL)"
                  value={customAuthority}
                  onChange={e=> setCustomAuthority(e.target.value)}
                  fullWidth size="small" placeholder="https://login.microsoftonline.com/{tenant}/v2.0"
                  helperText="Use full base including /v2.0 for Azure AD v2 endpoints or your B2C policy endpoint"
                />
              </Grid>
            )}
            <Grid item xs={12} md={6}>
              <TextField label="Authority Base URL" value={authorityInput} onChange={e=> setAuthorityInput(e.target.value)} fullWidth size="small" />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField label="Well-known URL" value={wellKnownUrl} fullWidth size="small" disabled />
            </Grid>
          </Grid>
          <Box sx={{ display: 'flex', gap: 2, mt: 3, flexWrap: 'wrap' }}>
            <Button variant="contained" startIcon={<RefreshIcon />} onClick={fetchMetadata} disabled={loading} sx={{ borderRadius: 2, minWidth: 200 }}>
              {loading ? 'Loading...' : 'Fetch Metadata'}
            </Button>
            <Button variant="text" color="inherit" size="small" onClick={()=> { setMetadata(null); setJwks(null); setJwtInput(''); setDecodedJwt(null); setKeyMatches([]); setSignatureValid(null); }}>
              Reset
            </Button>
          </Box>
          {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}
        </CardContent>
      </Card>

      {metadata && (
        <Card variant="outlined" sx={{ borderRadius: 2, mb: 3 }}>
          <CardContent sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', pb: 1, mb: 2, borderBottom: '1px solid', borderColor: 'divider' }}>
              <AssignmentIcon color="primary" sx={{ mr: 1 }} />
              <Typography variant="h6" sx={{ fontWeight: 600 }}>Discovery Document</Typography>
              <Button size="small" sx={{ ml: 'auto' }} onClick={()=> copy(JSON.stringify(metadata,null,2))}>Copy JSON</Button>
            </Box>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}><TextField label="Issuer" value={metadata.issuer || ''} size="small" fullWidth multiline /></Grid>
              <Grid item xs={12} md={6}><TextField label="Auth Endpoint" value={metadata.authorization_endpoint || ''} size="small" fullWidth multiline /></Grid>
              <Grid item xs={12} md={6}><TextField label="Token Endpoint" value={metadata.token_endpoint || ''} size="small" fullWidth multiline /></Grid>
              <Grid item xs={12} md={6}><TextField label="JWKS URI" value={metadata.jwks_uri || ''} size="small" fullWidth multiline /></Grid>
              <Grid item xs={12} md={6}><TextField label="End Session" value={metadata.end_session_endpoint || ''} size="small" fullWidth multiline /></Grid>
              <Grid item xs={12} md={6}><TextField label="UserInfo" value={metadata.userinfo_endpoint || ''} size="small" fullWidth multiline /></Grid>
            </Grid>
            <Divider sx={{ my: 3 }} />
            <Grid container spacing={2}>
              <Grid item xs={12} md={3}>
                <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>Response Types</Typography>
                <Typography variant="caption" sx={{ display: 'block' }}>{metadata.response_types_supported?.join(', ') || '—'}</Typography>
              </Grid>
              <Grid item xs={12} md={3}>
                <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>ID Token Algs</Typography>
                <Typography variant="caption" sx={{ display: 'block' }}>{metadata.id_token_signing_alg_values_supported?.join(', ') || '—'}</Typography>
              </Grid>
              <Grid item xs={12} md={3}>
                <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>Scopes</Typography>
                <Typography variant="caption" sx={{ display: 'block' }}>{metadata.scopes_supported?.slice(0,25).join(', ') || '—'}</Typography>
              </Grid>
              <Grid item xs={12} md={3}>
                <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>Claims</Typography>
                <Typography variant="caption" sx={{ display: 'block', maxHeight: 90, overflow: 'auto' }}>{metadata.claims_supported?.slice(0,30).join(', ') || '—'}</Typography>
              </Grid>
            </Grid>
            <Box sx={{ mt: 3 }}>
              <Button variant="outlined" onClick={fetchJwks} disabled={!metadata.jwks_uri || jwksLoading} sx={{ borderRadius: 2 }}>
                {jwksLoading ? 'Loading JWKS...' : 'Fetch JWKS'}
              </Button>
            </Box>
          </CardContent>
        </Card>
      )}

      {jwks && (
        <Card variant="outlined" sx={{ borderRadius: 2, mb: 3 }}>
          <CardContent sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', pb: 1, mb: 2, borderBottom: '1px solid', borderColor: 'divider' }}>
              <SecurityIcon color="primary" sx={{ mr: 1 }} />
              <Typography variant="h6" sx={{ fontWeight: 600 }}>JWKS Keys ({jwks.keys?.length || 0})</Typography>
              <Button size="small" sx={{ ml: 'auto' }} onClick={()=> copy(JSON.stringify(jwks,null,2))}>Copy JSON</Button>
            </Box>
            <Box sx={{ display: 'grid', gap: 1.5, gridTemplateColumns: { xs: '1fr', md: 'repeat(2,1fr)', lg: 'repeat(3,1fr)' } }}>
              {jwks.keys?.map(k => (
                <Paper key={k.kid || k.x5t || Math.random()} variant="outlined" sx={{ p: 1.5, borderRadius: 2 }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>{k.kid || 'no kid'}</Typography>
                  <Typography variant="caption" sx={{ display: 'block' }}>kty: {k.kty}</Typography>
                  {k.alg && <Typography variant="caption" sx={{ display: 'block' }}>alg: {k.alg}</Typography>}
                  {k.use && <Typography variant="caption" sx={{ display: 'block' }}>use: {k.use}</Typography>}
                  {k.n && <Typography variant="caption" sx={{ display: 'block' }}>n: {k.n.slice(0,40)}…</Typography>}
                  {k.e && <Typography variant="caption" sx={{ display: 'block' }}>e: {k.e}</Typography>}
                </Paper>
              ))}
            </Box>
          </CardContent>
        </Card>
      )}

      {/* JWT Analysis - Always visible, independent of metadata fetching */}
      <Card variant="outlined" sx={{ borderRadius: 2, mb: 3 }}>
        <CardContent sx={{ p: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', pb: 1, mb: 2, borderBottom: '1px solid', borderColor: 'divider' }}>
            <SecurityIcon color="primary" sx={{ mr: 1 }} />
            <Typography variant="h6" sx={{ fontWeight: 600 }}>JWT Analysis & Key Matching</Typography>
          </Box>
          {!metadata && (
            <Alert severity="info" sx={{ mb: 2 }}>
              <Typography variant="body2" sx={{ mb: 0.5 }}>
                You can paste any JWT here for instant analysis, even without fetching OIDC metadata.
              </Typography>
              <Typography variant="caption" color="text.secondary">
                💡 Try pasting an ID token from "Authenticate As" or access token from "Raw OAuth Tester" to decode and analyze it.
              </Typography>
            </Alert>
          )}
            <TextField label="Paste JWT (id_token / access_token)" value={jwtInput} onChange={e=> setJwtInput(e.target.value)} fullWidth multiline minRows={3} />
            <Box sx={{ mt: 2, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              <Button variant="contained" onClick={analyzeJwt} sx={{ borderRadius: 2 }}>Analyze</Button>
              <Button variant="outlined" onClick={()=> { setJwtInput(''); setDecodedJwt(null); setKeyMatches([]); setSignatureValid(null); }} sx={{ borderRadius: 2 }}>Clear</Button>
              <Tooltip title={!jwks?.keys ? "Fetch JWKS first to enable signature verification" : "Verify the JWT signature using the public key from JWKS"}>
                <span>
                  <Button 
                    variant="outlined" 
                    startIcon={<SecurityIcon />} 
                    disabled={!decodedJwt || !jwks?.keys || verifying} 
                    onClick={performSignatureVerification} 
                    sx={{ borderRadius: 2 }}
                  >
                    {verifying ? 'Verifying...' : 'Verify RS256 Signature'}
                  </Button>
                </span>
              </Tooltip>
            </Box>
            {decodedJwt && (
              <>
                {/* Token Analysis Summary */}
                <Box sx={{ mt: 2, p: 2, bgcolor: '#f0f7ff', borderRadius: 2, border: '1px solid #b3d9ff' }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1.5, color: 'primary.main' }}>
                    Token Analysis
                  </Typography>
                  <Grid container spacing={2}>
                    {/* Token Type */}
                    <Grid item xs={12} sm={6} md={4}>
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>Token Type</Typography>
                      <Chip 
                        size="small" 
                        label={tokenAnalysis?.label || 'JWT'}
                        color={tokenAnalysis?.color || 'default'}
                      />
                    </Grid>

                    {/* Algorithm */}
                    <Grid item xs={12} sm={6} md={4}>
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>Signing Algorithm</Typography>
                      <Chip size="small" label={decodedJwt.header?.alg || 'Unknown'} variant="outlined" />
                    </Grid>

                    {/* Key ID */}
                    {decodedJwt.header?.kid && (
                      <Grid item xs={12} sm={6} md={4}>
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>Key ID (kid)</Typography>
                        <Chip size="small" label={String(decodedJwt.header.kid).slice(0, 20) + (String(decodedJwt.header.kid).length > 20 ? '…' : '')} variant="outlined" />
                      </Grid>
                    )}

                    {/* Issuer */}
                    {decodedJwt.payload?.iss && (
                      <Grid item xs={12}>
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>Issuer</Typography>
                        <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.8rem', wordBreak: 'break-all' }}>
                          {String(decodedJwt.payload.iss)}
                        </Typography>
                      </Grid>
                    )}

                    {/* Audience */}
                    {decodedJwt.payload?.aud && (
                      <Grid item xs={12}>
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>Audience (Resource)</Typography>
                        <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.8rem', wordBreak: 'break-all' }}>
                          {Array.isArray(decodedJwt.payload.aud) ? decodedJwt.payload.aud.join(', ') : String(decodedJwt.payload.aud)}
                        </Typography>
                      </Grid>
                    )}

                    {/* Subject / User */}
                    {(decodedJwt.payload?.sub || decodedJwt.payload?.upn || decodedJwt.payload?.email || decodedJwt.payload?.preferred_username) && (
                      <Grid item xs={12} sm={6}>
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>Subject (User)</Typography>
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          {String(decodedJwt.payload.preferred_username || decodedJwt.payload.upn || decodedJwt.payload.email || decodedJwt.payload.sub || '')}
                        </Typography>
                      </Grid>
                    )}

                    {/* App/Client ID */}
                    {(decodedJwt.payload?.appid || decodedJwt.payload?.azp || decodedJwt.payload?.client_id) && (
                      <Grid item xs={12} sm={6}>
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>Client Application ID</Typography>
                        <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.8rem' }}>
                          {String(decodedJwt.payload.appid || decodedJwt.payload.azp || decodedJwt.payload.client_id || '')}
                        </Typography>
                      </Grid>
                    )}

                    {/* Issued At */}
                    {decodedJwt.payload?.iat && (
                      <Grid item xs={12} sm={6} md={4}>
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>Issued At</Typography>
                        <Typography variant="body2" sx={{ fontSize: '0.85rem' }}>
                          {new Date(Number(decodedJwt.payload.iat) * 1000).toLocaleString()}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {(() => {
                            const mins = Math.round((Date.now() - Number(decodedJwt.payload.iat) * 1000) / 60000);
                            return mins >= 0 ? `${mins}m ago` : `in ${Math.abs(mins)}m`;
                          })()}
                        </Typography>
                      </Grid>
                    )}

                    {/* Expires At */}
                    {decodedJwt.payload?.exp && (
                      <Grid item xs={12} sm={6} md={4}>
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>Expires At</Typography>
                        <Typography variant="body2" sx={{ fontSize: '0.85rem' }}>
                          {new Date(Number(decodedJwt.payload.exp) * 1000).toLocaleString()}
                        </Typography>
                        <Typography variant="caption" color={Date.now() > Number(decodedJwt.payload.exp) * 1000 ? 'error' : 'success.main'}>
                          {(() => {
                            const mins = Math.round((Number(decodedJwt.payload.exp) * 1000 - Date.now()) / 60000);
                            if (mins < 0) return `Expired ${Math.abs(mins)}m ago`;
                            return `Valid for ${mins}m`;
                          })()}
                        </Typography>
                      </Grid>
                    )}

                    {/* Not Before */}
                    {decodedJwt.payload?.nbf && (
                      <Grid item xs={12} sm={6} md={4}>
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>Not Before</Typography>
                        <Typography variant="body2" sx={{ fontSize: '0.85rem' }}>
                          {new Date(Number(decodedJwt.payload.nbf) * 1000).toLocaleString()}
                        </Typography>
                      </Grid>
                    )}

                    {/* Scopes (delegated permissions) */}
                    {decodedJwt.payload?.scp && (
                      <Grid item xs={12}>
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>Scopes (Delegated Permissions)</Typography>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                          {String(decodedJwt.payload.scp).split(/\s+/).filter(Boolean).map(s => (
                            <Chip key={s} label={s} size="small" color="primary" variant="outlined" />
                          ))}
                        </Box>
                      </Grid>
                    )}

                    {/* Roles (application permissions or app roles) */}
                    {decodedJwt.payload?.roles && Array.isArray(decodedJwt.payload.roles) && decodedJwt.payload.roles.length > 0 && (
                      <Grid item xs={12}>
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>Roles (Application Permissions / App Roles)</Typography>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                          {decodedJwt.payload.roles.map((r: any) => (
                            <Chip key={String(r)} label={String(r)} size="small" color="success" variant="outlined" />
                          ))}
                        </Box>
                      </Grid>
                    )}

                    {/* Tenant ID */}
                    {decodedJwt.payload?.tid && (
                      <Grid item xs={12} sm={6}>
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>Tenant ID</Typography>
                        <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.8rem' }}>
                          {String(decodedJwt.payload.tid)}
                        </Typography>
                      </Grid>
                    )}

                    {/* Object ID */}
                    {decodedJwt.payload?.oid && (
                      <Grid item xs={12} sm={6}>
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>Object ID (User)</Typography>
                        <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.8rem' }}>
                          {String(decodedJwt.payload.oid)}
                        </Typography>
                      </Grid>
                    )}

                    {/* Token Use */}
                    {decodedJwt.payload?.token_use && (
                      <Grid item xs={12} sm={6} md={4}>
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>Token Use (AWS Cognito)</Typography>
                        <Chip size="small" label={String(decodedJwt.payload.token_use)} variant="outlined" />
                      </Grid>
                    )}

                    {/* Version */}
                    {decodedJwt.payload?.ver && (
                      <Grid item xs={12} sm={6} md={4}>
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>Token Version</Typography>
                        <Chip size="small" label={`v${decodedJwt.payload.ver}`} variant="outlined" />
                      </Grid>
                    )}
                  </Grid>

                  {/* Interpretation hints */}
                  <Divider sx={{ my: 1.5 }} />
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontStyle: 'italic' }}>
                    {tokenAnalysis?.hint || '💡 JWT decoded successfully. Review the claims above to understand token purpose and permissions.'}
                  </Typography>
                </Box>

                {/* Raw Header/Payload */}
                <Grid container spacing={2} sx={{ mt: 1 }}>
                  <Grid item xs={12} md={6}>
                    <Paper variant="outlined" sx={{ p: 2, bgcolor: '#f8f9fa', borderRadius: 2, maxHeight: 300, overflow: 'auto' }}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>Header (Raw)</Typography>
                      <Box component="pre" sx={{ m: 0, fontFamily: 'monospace', fontSize: '0.75rem', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{JSON.stringify(decodedJwt.header, null, 2)}</Box>
                    </Paper>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Paper variant="outlined" sx={{ p: 2, bgcolor: '#f8f9fa', borderRadius: 2, maxHeight: 300, overflow: 'auto' }}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>Payload (Raw)</Typography>
                      <Box component="pre" sx={{ m: 0, fontFamily: 'monospace', fontSize: '0.75rem', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{JSON.stringify(decodedJwt.payload, null, 2)}</Box>
                    </Paper>
                  </Grid>
                </Grid>
              </>
            )}
            {keyMatches.length > 0 && (
              <Box sx={{ mt: 2 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>Matching Keys ({keyMatches.length})</Typography>
                <Stack spacing={1} sx={{ mt: 1 }}>
                  {keyMatches.map((m,i) => (
                    <Paper key={i} variant="outlined" sx={{ p: 1.5, borderRadius: 2 }}>
                      <Typography variant="caption" sx={{ display:'block' }}><strong>{m.key.kid}</strong> — {m.matchReasons.join(', ')}</Typography>
                    </Paper>
                  ))}
                </Stack>
              </Box>
            )}
            {signatureValid !== null && (
              <Alert severity={signatureValid ? 'success' : 'error'} sx={{ mt: 2 }}>
                {signatureValid ? 'Signature VALID (RS256)' : 'Signature INVALID or unsupported algorithm'}
              </Alert>
            )}
            {metadata && jwks?.keys && keyMatches.length === 0 && decodedJwt && (
              <Alert severity="warning" sx={{ mt: 2 }}>
                No matching keys found. The JWT's <code>kid</code> may not be in the current JWKS, or the algorithm may not match.
              </Alert>
            )}
          </CardContent>
        </Card>

      {/* Scenarios & Tips */}
      <Accordion 
        variant="outlined" 
        sx={{ borderRadius: 2, mt: 3, '&:before': { display: 'none' }, border: '1px solid #e2e8f0' }}
        defaultExpanded={false}
        disableGutters
      >
        <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ bgcolor: '#ffffff', borderBottom: '1px solid #e2e8f0' }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <TipsAndUpdatesIcon color="primary" sx={{ mr: 1 }} />
            <Typography variant="h6" sx={{ fontWeight: 600 }}>Scenarios & Tips</Typography>
          </Box>
        </AccordionSummary>
        <AccordionDetails>

          <Accordion disableGutters>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>Analyze Tokens from Other Tools</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                You can paste any JWT (ID token, access token) from other testing tools without fetching OIDC metadata first. Simply paste the token in the "JWT Analysis & Key Matching" section and click "Analyze" to:
              </Typography>
              <Box component="ul" sx={{ m: 0, pl: 2.5, mb: 1, '& li': { mb: 0.5 } }}>
                <li><Typography variant="body2">Decode and view all token claims</Typography></li>
                <li><Typography variant="body2">Identify token type (ID token vs access token)</Typography></li>
                <li><Typography variant="body2">Check expiration and validity window</Typography></li>
                <li><Typography variant="body2">View granted scopes and roles</Typography></li>
                <li><Typography variant="body2">Extract issuer, audience, and user information</Typography></li>
              </Box>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1, fontStyle: 'italic' }}>
                💡 Try copying tokens from "Authenticate As", "Raw OAuth Tester", or "Token Scope Requester" tools for quick analysis.
              </Typography>
            </AccordionDetails>
          </Accordion>

          <Accordion disableGutters>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>Validate Azure AD v2 Discovery</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                Use the tenant dropdown to select "common", "organizations", or your current tenant. Click "Fetch Metadata" to verify all required endpoints are present.
              </Typography>
              <Button size="small" variant="outlined" onClick={() => {
                setTenantSelection('organizations');
                openSnack('Authority set to organizations endpoint', 'info');
              }}>
                Apply
              </Button>
            </AccordionDetails>
          </Accordion>

          <Accordion disableGutters>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>Inspect Azure AD B2C Policy Metadata</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                Select "Custom tenant / B2C / Full URL" and enter your B2C authority like: <code>https://yourtenant.b2clogin.com/yourtenant.onmicrosoft.com/B2C_1_signupsignin1/v2.0</code>
              </Typography>
              <Button size="small" variant="outlined" onClick={() => {
                setTenantSelection('__custom__');
                setCustomAuthority('https://yourtenant.b2clogin.com/yourtenant.onmicrosoft.com/B2C_1_signupsignin1/v2.0');
                openSnack('B2C scenario applied - update tenant name and policy', 'info');
              }}>
                Apply Template
              </Button>
            </AccordionDetails>
          </Accordion>

          <Accordion disableGutters>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>Verify JWT Signature (RS256)</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                After fetching metadata and JWKS, paste an id_token or access_token into the JWT field. Click "Analyze" to decode and match the key by kid. Then click "Verify RS256 Signature" to cryptographically validate the token against the public key.
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                Note: Only RS256 algorithm is currently supported. ES256 and other algorithms will show as invalid.
              </Typography>
            </AccordionDetails>
          </Accordion>

          <Accordion disableGutters>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>Troubleshoot Custom IdP Integration</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                Enter your custom identity provider's base URL (e.g., <code>https://idp.example.com</code>). The tool will append <code>/.well-known/openid-configuration</code> automatically. Use this to verify your IdP correctly publishes OIDC metadata and JWKS for federation scenarios.
              </Typography>
            </AccordionDetails>
          </Accordion>

          <Accordion disableGutters>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>Compare Discovery Across Tenants</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                Fetch metadata for different tenants (dev, staging, prod) and compare issuer URIs, supported scopes, claims, and signing algorithms. This helps ensure consistency across environments.
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                Tip: Use the "Copy JSON" buttons to export metadata and perform diffs externally.
              </Typography>
            </AccordionDetails>
          </Accordion>

          <Accordion disableGutters>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>Understand Key Rotation</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                After fetching JWKS, note the number of keys and their <code>kid</code> values. Multiple keys indicate key rotation in progress. When you receive a JWT, check if its <code>kid</code> matches any key in the JWKS. Mismatches can indicate stale caches or rotation timing issues.
              </Typography>
            </AccordionDetails>
          </Accordion>

          <Accordion disableGutters>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>Debug Token Validation Errors</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                If your API rejects a token with "invalid signature" or "invalid issuer", use this tool to:
              </Typography>
              <Box component="ul" sx={{ m: 0, pl: 2.5, '& li': { mb: 0.5 } }}>
                <li><Typography variant="body2">Verify the issuer in the JWT payload matches the issuer in the discovery document</Typography></li>
                <li><Typography variant="body2">Confirm the JWT's <code>kid</code> exists in the current JWKS</Typography></li>
                <li><Typography variant="body2">Run signature verification to ensure the token hasn't been tampered with</Typography></li>
              </Box>
            </AccordionDetails>
          </Accordion>

        </AccordionDetails>
      </Accordion>

      {/* About */}
      <Accordion 
        variant="outlined" 
        sx={{ 
          borderRadius: 2, 
          bgcolor: '#f9f9f9', 
          mt: 2,
          '&:before': { display: 'none' },
          border: '1px solid #e2e8f0'
        }}
        disableGutters
      >
        <AccordionSummary 
          expandIcon={<ExpandMoreIcon />}
          sx={{
            bgcolor: '#ffffff',
            borderBottom: '1px solid #e2e8f0',
            '&.Mui-expanded': {
              borderBottom: '2px solid',
              borderColor: 'info.main'
            }
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <InfoOutlinedIcon color="primary" sx={{ mr: 1 }} />
            <Typography variant="h6" sx={{ fontWeight: 600 }}>About OIDC Metadata & JWKS Inspector</Typography>
          </Box>
        </AccordionSummary>
        <AccordionDetails sx={{ pt: 2 }}>
          <Typography variant="body1" sx={{ mb: 2, fontWeight: 500 }}>
            A specialized tool for inspecting OpenID Connect discovery documents, JSON Web Key Sets (JWKS), and validating JWT signatures.
          </Typography>
          <Alert severity="info" sx={{ mb: 3, borderRadius: 2 }}>
            <Typography variant="body2" sx={{ fontWeight: 600 }}>How this differs from Token Decoder</Typography>
            <Typography variant="body2" sx={{ mt: 0.5 }}>
              <strong>Use this inspector</strong> when you need to start from an <em>issuer / authority</em>: validate discovery endpoints, examine supported algorithms, fetch JWKS, compare tenants, inspect B2C policy authorities, or troubleshoot key rotation & configuration drift.
            </Typography>
            <Typography variant="body2" sx={{ mt: 1 }}>
              <strong>Use Token Decoder</strong> when you already have a JWT and want rapid claim decoding, semantic classification, quick JWKS fetch from its <code>iss</code>, and signature verification without first loading discovery manually.
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
              Shortcut: Need ecosystem / environment context? Stay here. Need to dissect a single token fast? Switch to Token Decoder.
            </Typography>
          </Alert>

          <Box sx={{ mb: 3 }}>
            <Typography variant="h6" sx={{ fontSize: '1rem', fontWeight: 600, mb: 1.5, color: 'primary.main' }}>
              What you can do:
            </Typography>
            <Box component="ul" sx={{ m: 0, pl: 2.5, '& li': { mb: 1 } }}>
              <li>
                <Typography variant="body2">
                  <strong>Fetch OIDC Discovery:</strong> Retrieve the OpenID Connect configuration document from any authority (Azure AD, B2C, custom IdP)
                </Typography>
              </li>
              <li>
                <Typography variant="body2">
                  <strong>Inspect JWKS:</strong> View all public keys used for token signing, including key IDs, algorithms, and key parameters
                </Typography>
              </li>
              <li>
                <Typography variant="body2">
                  <strong>Decode JWTs:</strong> Paste id_token or access_token to view decoded header and payload claims in readable format
                </Typography>
              </li>
              <li>
                <Typography variant="body2">
                  <strong>Match Keys:</strong> Automatically match JWT <code>kid</code> and <code>alg</code> claims against JWKS entries
                </Typography>
              </li>
              <li>
                <Typography variant="body2">
                  <strong>Verify Signatures:</strong> Cryptographically verify RS256 JWT signatures using Web Crypto API (client-side only)
                </Typography>
              </li>
              <li>
                <Typography variant="body2">
                  <strong>Multi-tenant Support:</strong> Quick dropdown for common, organizations, consumers, or custom authorities including B2C policies
                </Typography>
              </li>
              <li>
                <Typography variant="body2">
                  <strong>Export Metadata:</strong> Copy discovery documents and JWKS as JSON for external analysis or documentation
                </Typography>
              </li>
            </Box>
          </Box>

          <Box sx={{ mb: 3 }}>
            <Typography variant="h6" sx={{ fontSize: '1rem', fontWeight: 600, mb: 1.5, color: 'primary.main' }}>
              Common use cases:
            </Typography>
            <Box component="ul" sx={{ m: 0, pl: 2.5, '& li': { mb: 0.8 } }}>
              <li>
                <Typography variant="body2">
                  Diagnosing token validation failures in custom APIs or middleware
                </Typography>
              </li>
              <li>
                <Typography variant="body2">
                  Verifying OIDC metadata consistency across dev, staging, and production environments
                </Typography>
              </li>
              <li>
                <Typography variant="body2">
                  Understanding Azure AD B2C policy-specific endpoints and supported features
                </Typography>
              </li>
              <li>
                <Typography variant="body2">
                  Troubleshooting key rotation issues by inspecting current JWKS and matching token kids
                </Typography>
              </li>
              <li>
                <Typography variant="body2">
                  Validating custom identity provider integrations before production deployment
                </Typography>
              </li>
              <li>
                <Typography variant="body2">
                  Auditing supported scopes, claims, and response types for compliance documentation
                </Typography>
              </li>
              <li>
                <Typography variant="body2">
                  Testing JWT signature verification logic before implementing in backend services
                </Typography>
              </li>
            </Box>
          </Box>

          <Box sx={{ mb: 3 }}>
            <Typography variant="h6" sx={{ fontSize: '1rem', fontWeight: 600, mb: 1.5, color: 'primary.main' }}>
              Technical details:
            </Typography>
            <Box component="ul" sx={{ m: 0, pl: 2.5, '& li': { mb: 0.8 } }}>
              <li>
                <Typography variant="body2">
                  Discovery URL is automatically constructed by appending <code>/.well-known/openid-configuration</code> to the authority
                </Typography>
              </li>
              <li>
                <Typography variant="body2">
                  JWKS is fetched from the <code>jwks_uri</code> specified in the discovery document
                </Typography>
              </li>
              <li>
                <Typography variant="body2">
                  JWT decoding is performed client-side using base64url decoding; no server roundtrip required
                </Typography>
              </li>
              <li>
                <Typography variant="body2">
                  RS256 signature verification uses the Web Crypto API (<code>SubtleCrypto.verify</code>) with imported RSA public keys
                </Typography>
              </li>
              <li>
                <Typography variant="body2">
                  Readiness score is calculated based on presence of critical endpoints (authorization, token, JWKS, issuer)
                </Typography>
              </li>
              <li>
                <Typography variant="body2">
                  Currently supports RS256 only; ES256, PS256, and other algorithms planned for future releases
                </Typography>
              </li>
            </Box>
          </Box>

          <Box sx={{ p: 2, bgcolor: '#fff3cd', borderRadius: 1, border: '1px solid #ffeaa7', mb: 2 }}>
            <Typography variant="body2" sx={{ fontWeight: 500, mb: 1 }}>
              💡 Pro tip:
            </Typography>
            <Typography variant="body2">
              Use the "Scenarios & Tips" section above to quickly set up common inspection workflows. 
              For B2C policies, remember to include the full policy path in your custom authority URL.
            </Typography>
          </Box>

          <Box sx={{ p: 2, bgcolor: '#e7f3ff', borderRadius: 1, border: '1px solid #b3d9ff', mb: 2 }}>
            <Typography variant="body2" sx={{ fontWeight: 500, mb: 1 }}>
              🔒 Security note:
            </Typography>
            <Typography variant="body2">
              All JWT decoding and signature verification is performed entirely in your browser using standard Web APIs. 
              Tokens are never sent to any server. However, avoid pasting production tokens containing sensitive user data unless necessary for troubleshooting.
            </Typography>
          </Box>

          <Box sx={{ p: 2, bgcolor: '#f0f0f0', borderRadius: 1, border: '1px solid #d0d0d0' }}>
            <Typography variant="body2" sx={{ fontWeight: 500, mb: 1 }}>
              🔍 Troubleshooting tips:
            </Typography>
            <Box component="ul" sx={{ m: 0, pl: 2.5, '& li': { mb: 0.5 } }}>
              <li><Typography variant="body2">If CORS errors occur, the authority may not allow browser-based discovery fetches</Typography></li>
              <li><Typography variant="body2">Signature verification failures with correct kid often indicate clock skew or token tampering</Typography></li>
              <li><Typography variant="body2">Missing endpoints in discovery may indicate incomplete OIDC provider implementation</Typography></li>
              <li><Typography variant="body2">Multiple keys in JWKS during rotation period is normal; always cache JWKS and retry on kid mismatch</Typography></li>
            </Box>
          </Box>
        </AccordionDetails>
      </Accordion>

      <Box sx={{ textAlign: 'center', opacity: 0.6, py: 2 }}>
        <Typography variant="caption">All operations performed client-side. Avoid pasting sensitive production tokens unnecessarily.</Typography>
      </Box>
    </Container>
  );
};

export default OidcMetadataInspector;
